import { motion } from "framer-motion";
import { MonitorPlay, BrainCircuit, Check } from "lucide-react";

const PILLARS = [
  {
    icon: MonitorPlay,
    badge: "The Hardware",
    name: "On The Brink Media Group LLC",
    desc: "Physical display products engineered for impact — LED walls, smart classrooms, and enterprise-grade AV solutions installed at scale.",
    points: ["Large-format LED walls", "Smart classroom systems", "Enterprise AV infrastructure"],
    img: "https://images.unsplash.com/photo-1680992046615-065f58bcb4d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: BrainCircuit,
    badge: "The Brainpower",
    name: "On The Brink Marketing Group, Inc.",
    desc: "An AI-first marketing and growth consultancy (OTBMG) that maximizes the messaging displayed on those physical products and across every channel.",
    points: ["Agentic AI marketing", "Growth & retention strategy", "Creative & commercial judgment"],
    img: "https://images.unsplash.com/photo-1717565813196-8944b57877f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    logo: "https://customer-assets.emergentagent.com/job_growth-partners-hub/artifacts/ac9hz044_OTBMarketingGroup%20Logo%20400x101.jpg",
  },
];

export default function Pillars() {
  return (
    <section id="ecosystem" className="py-24 md:py-32 bg-slate-50 border-y border-slate-200" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-ember">Our Ecosystem</p>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl">
            The Two Pillars of On The Brink.
          </h2>
          <p className="mt-5 text-lg text-slatey">
            A hybrid brand architecture uniting the physical and the intelligent — the screens that command attention,
            and the strategy that fills them with results.
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          {PILLARS.map((p, i) => (
            <motion.div key={p.name}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`pillar-card-${i}`}
              className="group flex flex-col border border-slate-200 bg-white">
              <div className="relative h-52 overflow-hidden">
                <img src={p.img} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-ink/30" />
                <span className="absolute top-4 left-4 bg-white px-3 py-1 text-xs uppercase tracking-[0.15em] font-semibold text-ink">
                  {p.badge}
                </span>
              </div>
              <div className="p-8 md:p-10 flex flex-col flex-1">
                <p.icon className="h-7 w-7 text-ember" />
                <h3 className="mt-5 font-heading font-bold text-ink text-xl sm:text-2xl">{p.name}</h3>
                {p.logo && (
                  <img src={p.logo} alt={`${p.name} logo`} className="mt-4 h-9 w-auto object-contain self-start" />
                )}
                <p className="mt-4 text-slatey leading-relaxed">{p.desc}</p>
                <ul className="mt-6 space-y-3">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-3 text-sm text-ink">
                      <Check className="h-4 w-4 text-ember shrink-0" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
